// welcomeContent.js — plain-language copy for the SME first-run onboarding
// experience (WP3). This module is DATA ONLY: no JSX, no imports, so it can be
// unit-tested in isolation and reused by both the full-overlay FirstRunWelcome
// and the compact StartHereStrip on the home page.
//
// Audience: government subject-matter experts evaluating DHA PRIME for the first
// time. Copy rules (05 §6 "plain language first"): no raw field names, no enum
// values, no internal IDs, no developer jargon. Every sentence should read the
// way a program office colleague would explain the tool out loud. This is the
// first thing an evaluator reads — it is worth getting exactly right.

// One or two short sentences answering "what am I looking at?" for someone who
// has never seen the product. Rendered through wrapGlossaryTerms in pane 1, so
// any known term (e.g. "RAPID") gets an inline definition automatically.
export const whatIsPrime =
  "DHA PRIME is the Defense Health Agency's Program Resource and Intelligence Management Engine — a single place to request work, follow it from idea to delivery, and see how it maps to the mission. AI drafts the paperwork and surfaces risk early; people stay in charge of every decision.";

// Per-persona confirmation copy for pane 1. Keyed by the five persona keys from
// lib/personaKey.js. `title` is a short, friendly role name; `plainSummary` is a
// single jargon-free sentence describing what this person does in PRIME; and
// `firstStop` is the one screen we recommend they open first.
export const personaCards = Object.freeze({
  executive_sponsor: {
    title: 'Executive Sponsor',
    plainSummary:
      'You watch the health of the whole portfolio, approve the big decisions, and pull briefings for leadership.',
    firstStop: { route: '/executive', label: 'Open your Command Center' },
  },
  delivery_leader: {
    title: 'Delivery / Portfolio Leader',
    plainSummary:
      'You keep delivery on track — sprint health, roadmap, and readiness for each gate review.',
    firstStop: { route: '/delivery', label: 'Open your delivery workspace' },
  },
  data_steward: {
    title: 'Data Steward / Admin',
    plainSummary:
      'You keep the data trustworthy — connect the source systems, check where numbers come from, and review access.',
    firstStop: { route: '/admin/data', label: 'Open connectors' },
  },
  system_admin: {
    title: 'System Admin',
    plainSummary:
      'You manage who can see and do what, and you can audit every change made in the workspace.',
    firstStop: { route: '/admin/rbac', label: 'Open access controls' },
  },
  pending_access: {
    title: 'Getting Access',
    plainSummary:
      'Your access request is being reviewed. You can track its status and see a limited preview until it is approved.',
    firstStop: { route: '/access/status', label: 'Check your access status' },
  },
});

// The three guided journeys plus the short orientation. Each entry carries the
// tourId the tour engine listens for (dispatched via the prime:start-tour
// custom event), an estimated time, and a one-line "why you'd take this" blurb.
export const journeyCards = Object.freeze([
  {
    tourId: 'j1-lifecycle',
    title: 'Follow a request from idea to delivery',
    minutes: 4,
    blurb:
      'Walk one piece of work through every stage — from the first ask to the team delivering it — so the lifecycle makes sense end to end.',
  },
  {
    tourId: 'j2-data-to-decision',
    title: 'See how data becomes a decision',
    minutes: 5,
    blurb:
      'Trace a number from its source system all the way to the briefing where a leader acts on it, and see how PRIME shows its work.',
  },
  {
    tourId: 'j3-first-week',
    title: 'Your first week in PRIME',
    minutes: 3,
    blurb:
      'The handful of screens and habits that make the first few days productive, tailored to what your role does day to day.',
  },
]);

// The 2-minute orientation offered as the primary "start here" action. Kept
// separate from journeyCards because it is the lightweight, always-first option.
export const orientation = Object.freeze({
  tourId: 'orientation',
  title: 'Take the 2-minute orientation',
  minutes: 2,
  blurb: 'A quick tour of the sidebar, search, and the PRIME Copilot so nothing feels unfamiliar.',
});

// Pane 3 copy — how to find your way around after the welcome closes. Points to
// the essentials navigation, the "show all features" escape hatch, the Help
// center, and Ask PRIME. Plain wording only; the routes are wired by the
// component, not stored here.
export const findingYourWay = Object.freeze({
  heading: 'Finding your way around',
  intro:
    'The sidebar starts with the essentials — the screens most people use every day. When you need more, choose "Show all features" at the bottom of the sidebar to reveal everything your role can reach.',
  helpPointer:
    'The Help & getting started center collects these tours, short how-tos, and answers to common questions. You can reopen it any time.',
  askPointer:
    'Stuck on wording or an acronym? Ask PRIME — the copilot in the corner — can explain anything on the screen in plain language.',
});
